import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Role } from "@/constants/rolePermissions";
import { Income } from "@/hooks/admin/shared/useIncome";
import { useProperties } from "@/hooks/useProperties";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import BaseFilters from "@/components/v2/molecules/admin/shared/BaseFilters";
import { useMemo } from "react";

interface IncomeFiltersProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  hasActiveFilters: boolean;
  paymentTypes: Income["payment_type"][];
  role: Role;
  searchTerm: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  selectedPaymentType: Income["payment_type"] | "";
  organizations: any[];
  contracts: any[];
  clearFilters: () => void;
  setDateFrom: (value: Date | undefined) => void;
  setDateTo: (value: Date | undefined) => void;
  setSearchTerm: (value: string) => void;
  setSelectedProperties: (propertyIds: string[]) => void;
  setSelectedOrganizations: (organizationIds: string[]) => void;
  setSelectedPaymentType: (value: Income["payment_type"] | "") => void;
}

const IncomeFilters = ({
  dateFrom,
  dateTo,
  hasActiveFilters,
  paymentTypes,
  searchTerm,
  selectedProperties,
  selectedOrganizations,
  selectedPaymentType,
  organizations,
  contracts,
  clearFilters,
  setDateFrom,
  setDateTo,
  setSearchTerm,
  setSelectedProperties,
  setSelectedOrganizations,
  setSelectedPaymentType,
}: IncomeFiltersProps) => {
  const { data: properties = [] } = useProperties();

  // Filter properties based on selected organizations
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      return properties;
    }

    const propertyIdsInOrganizations = contracts
      .filter((contract: any) =>
        contract.status === 'active' &&
        selectedOrganizations.includes(contract.organization_id)
      )
      .map((contract: any) => contract.property_id);

    return properties.filter((property: any) =>
      propertyIdsInOrganizations.includes(property.id)
    );
  }, [properties, selectedOrganizations, contracts]);

  const handleDateRangeChange = (from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
  };

  return (
    <BaseFilters
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Buscar reservación..."
      searchWidth="w-[200px]"
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateRangeChange={handleDateRangeChange}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={clearFilters}
    >
      <OrganizationsMultiSelect
        organizations={organizations}
        selectedOrganizations={selectedOrganizations}
        onSelectionChange={setSelectedOrganizations}
      />

      <PropertiesMultiSelect
        properties={filteredProperties}
        selectedProperties={selectedProperties}
        onSelectionChange={setSelectedProperties}
      />

      <Select
        value={selectedPaymentType || "all"}
        onValueChange={(value) =>
          setSelectedPaymentType(
            value === "all" ? "" : (value as Income["payment_type"]),
          )
        }
      >
        <SelectTrigger className="w-[180px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0">
          <SelectValue placeholder="Todos los tipos de pago" />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">Todos los tipos de pago</SelectItem>
          {paymentTypes.map((type) => (
            <SelectItem key={type} value={type}>
              {type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </BaseFilters>
  );
};

export default IncomeFilters;

